import { useMemo } from 'react';

import type { UnifiedBalancesInputFilters } from './useUnifiedBalances';

import type { PortfolioHistory } from '../../../api/hyperliquid';
import { useHyperliquidPortfolioHistory } from '../../../api/hyperliquid';
import { useAccountAddresses } from '../../../api/user/hooks';
import { useOptimisticUserUiState } from '../../../api/user/userQueries';
import { useUnifiedBalancesStore } from '../stores/useUnifiedBalancesStore';
import type { UsdBalanceTotals } from '../stores/utils/computeUsdTotals';

export type UsdBalanceTotalsInputFilters = UnifiedBalancesInputFilters;

/**
 * Hook that calculates USD Totals from unified balances and asset rates.
 * Uses caching to avoid recomputing totals for the same filter configuration.
 *
 * @param filters - Optional filters to apply to the balances (custody, chain, walletIds)
 * @returns USD balance totals including perps 24h PnL
 */
export function useUsdBalanceTotals(
  filters?: UsdBalanceTotalsInputFilters
): UsdBalanceTotals {
  const addresses = useAccountAddresses(filters);
  const { data: userUiState } = useOptimisticUserUiState();
  const { portfolioHistoryByWallet } = useHyperliquidPortfolioHistory();

  const includeLockedNfts =
    userUiState?.patronTotalBalancesSettings?.includeLockedNfts ?? true;

  const totalPerpsPnl24h = useMemo(() => {
    const walletIds = new Set(
      addresses
        .map((address) => address.walletId)
        .filter((id): id is string => id !== null)
    );

    return getTotalPerpsPnl24h(portfolioHistoryByWallet, walletIds);
  }, [addresses, portfolioHistoryByWallet]);

  const cachedTotals = useUnifiedBalancesStore((state) =>
    state.selectUsdTotals(addresses, totalPerpsPnl24h, includeLockedNfts)
  );

  return useMemo(() => {
    if (!cachedTotals) {
      return {
        totalUsdTokenBalance: undefined,
        totalUsdPositionBalance: undefined,
        totalNftExcludingPatronsUsdBalance: undefined,
        totalPatronNftUsdBalance: undefined,
        totalUsdBalance: undefined,
        totalTokenAndPositionUsdBalance24hChange: undefined,
        totalTokenAndPositionUsdBalance24hPercentageChange: undefined,
        totalPortfolioUsd24hChange: undefined,
        totalPortfolioUsd24hPercentageChange: undefined,
        totalUsdBalanceOfficialOnly: undefined,
        isLoading: true,
        isStale: undefined,
      };
    }

    return cachedTotals;
  }, [cachedTotals]);
}

/**
 * Calculate total 24h PnL from portfolio history map, filtered by walletIds
 */
function getTotalPerpsPnl24h(
  portfolioHistoryByWallet: Map<string, PortfolioHistory>,
  walletIds: Set<string>
) {
  if (walletIds.size === 0 || portfolioHistoryByWallet.size === 0) {
    return 0;
  }

  let totalPnl = 0;

  walletIds.forEach((walletId) => {
    const portfolioHistory = portfolioHistoryByWallet.get(walletId);
    if (!portfolioHistory) {
      return;
    }

    const pnlHistory = portfolioHistory?.day?.pnlHistory;
    if (!pnlHistory || !pnlHistory.length) {
      return;
    }

    // Calculate 24h PnL change from Hyperliquid perps
    const safeNumber = (value: unknown): number => {
      const num = Number(value);
      return isNaN(num) || !isFinite(num) ? 0 : num;
    };

    const sortedPnlHistory = [...pnlHistory].sort((a, b) => a[0] - b[0]);
    const lastPnlTuple = sortedPnlHistory[sortedPnlHistory.length - 1];
    const pnlValue = lastPnlTuple[1]; // Get the PnL string value from tuple
    totalPnl += safeNumber(pnlValue);
  });

  return totalPnl;
}
